/**
 * @file LabelSheetStartPicker.tsx
 * @description «Начать с N-й наклейки» для одного шаблона: числовое поле и
 *   схема листа (phase19.22, W17-5a; ui-design §1, риск «на 360 мини 10×14
 *   мелкая для тапа»).
 *
 * Лист самоклейки часто начат: первые наклейки уже сняты. Главное — числовое
 * поле 56px. Схема показывает, какие клетки пропустим (серые) и откуда
 * начнём (зелёная); тап по клетке задаёт N только на `≥sm` и только у
 * стандартного и компактного листов — клетка мини 20×20 на телефоне меньше
 * пальца, там схема только для чтения.
 */

import {
  LABEL_TEMPLATE_META,
  type LabelTemplate,
} from "../../../lib/api-labels";
import { clampStartAt } from "../../../lib/label-sheets";
import { cn } from "@/lib/utils";

interface Props {
  template: LabelTemplate;
  value: number;
  onChange: (value: number) => void;
}

export function LabelSheetStartPicker({ template, value, onChange }: Props) {
  const meta = LABEL_TEMPLATE_META[template];
  const start = clampStartAt(value, meta.perSheet);
  // Клетка мини — 2 мм на схеме: тапом не попасть, выбор только полем.
  const tappable = template !== "MINI";
  const cells = Array.from({ length: meta.perSheet }, (_, i) => i + 1);

  return (
    <div className="flex items-start gap-3">
      <label className="flex shrink-0 flex-col gap-1">
        <span className="text-sm">{meta.title}</span>
        <input
          type="text"
          inputMode="numeric"
          value={String(start)}
          onChange={(e) =>
            onChange(clampStartAt(Number(e.target.value.replace(/\D/g, "") || "1"), meta.perSheet))
          }
          aria-label={`Начать с наклейки, ${meta.title}`}
          className="h-14 w-20 rounded-lg bg-surface-lowest text-center font-mono text-base tabular-nums ring-ghost focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <span className="text-xs text-on-surface-variant">из {meta.perSheet}</span>
      </label>
      <div
        className="grid min-w-0 flex-1 gap-0.5 self-center"
        style={{ gridTemplateColumns: `repeat(${meta.cols}, minmax(0, 1fr))`, maxWidth: meta.cols * 28 }}
        aria-hidden={!tappable}
      >
        {cells.map((n) => {
          const cls = cn(
            "rounded-[2px]",
            template === "MINI" ? "h-1.5" : "h-3",
            n < start ? "bg-on-surface/25" : n === start ? "bg-primary" : "bg-surface-low ring-ghost",
          );
          return tappable ? (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              aria-label={`Начать с ${n}-й наклейки`}
              // Тап по клетке — только на ≥sm; на телефоне схема для чтения.
              className={cn(cls, "pointer-events-none sm:pointer-events-auto sm:cursor-pointer")}
              tabIndex={-1}
            />
          ) : (
            <span key={n} className={cls} />
          );
        })}
      </div>
    </div>
  );
}
